import React from "react";
/**
 * Hook that can create a collection of event handlers to notify when the window resizes and
 * either hides or reveals an item at a specific index on the AppBar
 */
export interface MediaQueryListener {
  /**
   * Vector of boolean values signifying elements that are visible (true) or not (false)
   * @remarks Index of the flag correlates to the index of the element in the collection
   */
  visibilityVector: boolean[];
  /**
   * Sets the current bottom padding used in calculating the collection of media query listeners
   */
  setBottomPadding: React.Dispatch<React.SetStateAction<number>>;
  /**
   * Sets the current top margin (top position) used for base trigger calculations
   */
  setTopMargin: React.Dispatch<React.SetStateAction<number>>;
  /**
   * Sets the height of elements used to calculate the media queries
   */
  setElementHeight: React.Dispatch<React.SetStateAction<number>>;
  /**
   * Sets the number of elements to create media query listeners for
   */
  setElementCount: React.Dispatch<React.SetStateAction<number | undefined>>;
  /**
   * Sets the number of elements from the beginning of the visibility vector that are always 'visible'
   */
  setMinimumVisibleElementCount: React.Dispatch<React.SetStateAction<number>>;
}
/**
 * Creates a collection of event handlers to notify when the window resizes and
 * either hides or reveals an item at a specific index on the AppBar
 * @returns MediaQueryListener hook
 */
export const useDynamicMediaQueryListener = (
  matchMedia: (query: string) => MediaQueryList
): MediaQueryListener => {
  const [bottomPadding, setBottomPadding] = React.useState<number>(0);
  const [topMargin, setTopMargin] = React.useState<number>(0);
  const [elementHeight, setElementHeight] = React.useState<number>(0);
  const [elementCount, setElementCount] = React.useState<number | undefined>();
  const [minimumVisibleElementCount, setMinimumVisibleElementCount] =
    React.useState<number>(0);
  const [visibilityVector, setVisibilityVector] = React.useState<boolean[]>([]);
  /**
   * Creates a query collection containing each elements media query
   */
  const queryList = React.useMemo(() => {
    if (elementCount === undefined) {
      return undefined;
    }
    const queries: string[] = [];
    for (let i = 0; i < elementCount; i++) {
      const threshold = elementHeight * (i + 1) + bottomPadding + topMargin;
      const query = `(max-height: ${threshold}px)`;
      queries.push(query);
    }
    return queries;
  }, [bottomPadding, elementCount, elementHeight, topMargin]);
  /**
   * Builds the visibility vector based on queries within the queryList
   */
  const buildVisibilityVector = React.useCallback(() => {
    if (queryList === undefined) {
      return;
    }
    let vector = queryList.map((mediaQuery) => {
      return !matchMedia(mediaQuery).matches;
    });
    vector = vector.fill(true, 0, minimumVisibleElementCount);
    if (vector.length > 0 && visibilityVector.join() !== vector.join()) {
      setVisibilityVector(vector);
    }
  }, [matchMedia, minimumVisibleElementCount, queryList, visibilityVector]);
  /**
   * Creates an event listener for each query in queryList to rebuild
   * the visibility vector when the window resizes
   * @remarks mediaQuery.addEventListener is mediaQuery.addListener for Safari 13 and below
   */
  React.useEffect(() => {
    const mediaQueries: MediaQueryList[] = [];
    queryList?.forEach((query) => {
      const mediaQuery = matchMedia(query);
      try {
        mediaQuery.onchange = buildVisibilityVector;
      } catch {
        try {
          mediaQuery.addListener(buildVisibilityVector);
        } catch {}
      }
      mediaQueries.push(mediaQuery);
    });
    buildVisibilityVector();
    return () => {
      mediaQueries.forEach((mediaQuery) => {
        try {
          mediaQuery.onchange = null;
        } catch {
          try {
            mediaQuery.removeListener(buildVisibilityVector);
          } catch {}
        }
      });
      mediaQueries.splice(0);
    };
  }, [matchMedia, buildVisibilityVector, queryList]);
  return {
    setBottomPadding,
    setTopMargin,
    setElementHeight,
    setElementCount,
    setMinimumVisibleElementCount,
    visibilityVector,
  };
};
